/**
 * The full correction of a finished challenge (D-106), TCF or IELTS: for each question, the
 * answer given, the right answer and the explanation (« Explanation to come » when missing).
 * The reading of the rows is shared with the phone (@agencyos/core `parseChallengeReview`).
 */

import { type ChallengeReviewRow, hasPictures } from "@agencyos/core";
import { CheckCircle2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ExamPictureView, PictureOptions } from "./ExamPicture";
import { SectionTitle } from "./ui";
import { InMyLanguage } from "./ielts/InMyLanguage";

export function ChallengeReview({ rows, lang }: { rows: ChallengeReviewRow[]; lang: string }) {
  const { t } = useTranslation();
  if (!rows.length) return null;
  return (
    <section className="ac-section" aria-labelledby="academy-challenge-review">
      <SectionTitle id="academy-challenge-review">{t("listening.review.challengeTitle")}</SectionTitle>
      <ol className="ac-review-list">
        {rows.map((row) => (
          <li key={row.id} className={`ac-review ac-surface${row.correct ? " ac-review--right" : " ac-review--wrong"}`}>
            <p className="ac-review__head">
              <span className="ac-review__mark" aria-hidden="true">
                {row.correct ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
              </span>
              {row.level && <strong className="ac-caption">{row.level}</strong>}
              <span className="visually-hidden">{row.correct ? t("academy.player.right") : t("academy.results.wrongShort")}</span>
            </p>
            <p className="ac-review__stem" lang={lang}>
              {row.stem}
            </p>
            <ExamPictureView picture={row.image} lang={lang} size="medium" />
            {hasPictures(row.optionImages) && (
              <PictureOptions
                name={`cr-${row.id}`}
                options={row.options}
                images={row.optionImages}
                answer={row.rightIndex}
                given={row.givenIndex}
                showText
                lang={lang}
              />
            )}
            <dl className="ac-review__answers" lang={lang}>
              <div>
                <dt className="ac-caption">{t("listening.review.given")}</dt>
                <dd>{row.answered ? row.given : t("academy.results.noAnswer")}</dd>
              </div>
              <div>
                <dt className="ac-caption">{t("listening.review.right")}</dt>
                <dd>{row.right}</dd>
              </div>
            </dl>
            {row.explanation ? (
              <>
                <p className="ac-review__why">{row.explanation}</p>
                <InMyLanguage text={row.explanation} source="fr" examLanguage="fr" kind="correction" />
              </>
            ) : (
              <p className="ac-review__why ac-review__why--pending">{t("listening.review.explanationSoon")}</p>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
